import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Bell, Download, FileSignature, Mail, Plus, RefreshCw, Send, Trash2,
  UploadCloud, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFacility } from '../context/FacilityContext';
import documentSigningService from '../services/documentSigningService';
import AddOnPrompt from '../components/AddOnPrompt';
import PlacementPages, { toProvider } from '../components/signing/PlacementPages';
import { formatDateTime } from '../utils/datetime';

/**
 * Document signing — send any document for signature and track it.
 *
 * A paid add-on, connected per facility (Settings → Third-party apps). It is
 * this product's own screen: the operator never sees the provider. Reads and
 * drafts go through the server passthrough; sending, reminders and signed
 * copies run on the server, which emails each person their own link
 * (services/documentSigningService).
 */

const STATUS = {
  draft: { label: 'Draft', cls: 'bg-slate-50 text-slate-500 ring-slate-200' },
  sent: { label: 'Waiting for signatures', cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
  partially_signed: { label: 'Partly signed', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  completed: { label: 'Completed', cls: 'bg-green-50 text-green-700 ring-green-200' },
  voided: { label: 'Cancelled', cls: 'bg-slate-50 text-slate-400 ring-slate-200' },
  expired: { label: 'Expired', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
};
const SIGNER = {
  pending: 'Not sent yet', sent: 'Sent', viewed: 'Opened', signed: 'Signed',
  declined: 'Declined', bounced: 'Email bounced', waiting: 'Waiting for their turn',
};
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'waiting', label: 'Waiting', match: ['sent', 'partially_signed'] },
  { key: 'completed', label: 'Completed', match: ['completed'] },
  { key: 'draft', label: 'Drafts', match: ['draft'] },
  { key: 'closed', label: 'Cancelled / expired', match: ['voided', 'expired'] },
];

const errText = (e, fallback) =>
  e?.response?.data?.error?.message || e?.response?.data?.detail || fallback;
// Times show in the FACILITY's zone, never the browser's (owner rule).
const makeWhen = (tz) => (v) => (v ? formatDateTime(v, tz) : '—');

const StatusPill = ({ status }) => {
  const s = STATUS[status] || { label: status || '—', cls: 'bg-slate-50 text-slate-500 ring-slate-200' };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${s.cls}`}>
      {s.label}
    </span>
  );
};

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

/* ------------------------------------------------------------- the list */

const DocumentList = ({ facilityId, tz, onOpen }) => {
  const when = makeWhen(tz);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await documentSigningService.list(facilityId);
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
      setError(errText(e, 'Could not load documents.'));
    }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return (items || []).filter((d) => !f?.match || f.match.includes(d.status));
  }, [items, filter]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-full text-[12px] border ${
              filter === f.key ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {f.label}
          </button>
        ))}
        <button type="button" onClick={load} title="Refresh"
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 text-[12px] text-slate-600 hover:bg-slate-200">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {error && <p className="mb-3 text-[13px] text-rose-700">{error}</p>}
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {items === null ? (
          <p className="p-6 text-center text-[13px] text-slate-400">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="p-6 text-center text-[13px] text-slate-400">
            {items.length === 0 ? 'Nothing sent for signature yet.' : 'Nothing in this view.'}
          </p>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Document</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Sent</th>
                <th className="px-4 py-2 text-left font-medium">Completed / expires</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <tr key={d.id} onClick={() => onOpen(d.id)}
                  className="border-t border-slate-100 cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{d.title || 'Untitled'}</td>
                  <td className="px-4 py-2.5"><StatusPill status={d.status} /></td>
                  <td className="px-4 py-2.5 text-slate-500">{when(d.sent_at)}</td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {d.completed_at ? when(d.completed_at) : (d.expires_at ? `expires ${when(d.expires_at)}` : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {items && items.length >= 100 && (
        <p className="mt-2 text-[12px] text-slate-400">Showing the newest 100.</p>
      )}
    </div>
  );
};

/* ----------------------------------------------------------- one document */

const DocumentDetail = ({ facilityId, tz, id, onBack }) => {
  const when = makeWhen(tz);
  const [env, setEnv] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await documentSigningService.get(facilityId, id);
      setEnv(res.data);
    } catch (e) {
      setError(errText(e, 'Could not load this document.'));
    }
  }, [facilityId, id]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const res = await fn();
      if (ok) toast.success(typeof ok === 'function' ? ok(res) : ok);
      await load();
    } catch (e) {
      toast.error(errText(e, 'That did not work.'));
    } finally {
      setBusy('');
    }
  };

  const download = (stage, name) => run(`pdf-${stage}`, async () => {
    const res = await documentSigningService.pdf(facilityId, id, stage);
    saveBlob(res.data, name);
  });

  if (error) {
    return (
      <div>
        <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[13px] text-blue-700"><ArrowLeft className="h-4 w-4" /> All documents</button>
        <p className="text-[13px] text-rose-700">{error}</p>
      </div>
    );
  }
  if (!env) return <p className="p-6 text-center text-[13px] text-slate-400">Loading…</p>;

  const live = ['sent', 'partially_signed'].includes(env.status);
  const fileBase = (env.title || 'document').replace(/[^A-Za-z0-9 _-]+/g, '').trim() || 'document';
  const reminded = (res) => {
    const list = res?.data?.reminded || [];
    const sent = list.filter((r) => r.emailed).length;
    return sent ? `Reminder emailed to ${sent} signer${sent > 1 ? 's' : ''}.` : 'No reminder was emailed — see Tasks.';
  };

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[13px] text-blue-700">
        <ArrowLeft className="h-4 w-4" /> All documents
      </button>
      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[16px] font-semibold text-slate-900">{env.title}</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">
              Created {when(env.created_at)}{env.sent_at ? ` · sent ${when(env.sent_at)}` : ''}
              {env.completed_at ? ` · completed ${when(env.completed_at)}` : ''}
              {live && env.expires_at ? ` · signing open until ${when(env.expires_at)}` : ''}
            </p>
          </div>
          <StatusPill status={env.status} />
        </div>
        {env.message && <p className="mt-3 text-[13px] text-slate-600 whitespace-pre-line">{env.message}</p>}
        {env.void_reason && <p className="mt-3 text-[13px] text-slate-500">Cancelled: {env.void_reason === 'declined_by_recipient' ? 'a signer declined' : env.void_reason}</p>}

        <h3 className="mt-5 mb-2 text-[13px] font-semibold text-slate-800">Signers</h3>
        <div className="rounded-lg border border-slate-100 overflow-hidden">
          <table className="w-full text-[13px]">
            <tbody>
              {(env.from_party?.signed_at) && (
                <tr className="border-b border-slate-100">
                  <td className="px-3 py-2 text-slate-800">{env.from_party.name} <span className="text-slate-400">(you)</span></td>
                  <td className="px-3 py-2 text-slate-500">{env.from_party.email}</td>
                  <td className="px-3 py-2 text-green-700">Signed {when(env.from_party.signed_at)}</td>
                  <td />
                </tr>
              )}
              {(env.recipients || []).map((r) => (
                <tr key={r.index} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2 text-slate-800">{r.name}</td>
                  <td className="px-3 py-2 text-slate-500">{r.email}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {SIGNER[r.status] || r.status}
                    {r.signed_at ? ` ${when(r.signed_at)}` : (r.viewed_at ? ` ${when(r.viewed_at)}` : '')}
                    {r.decline_reason ? ` — “${r.decline_reason}”` : ''}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {live && ['sent', 'viewed', 'bounced'].includes(r.status) && (
                      <button type="button" disabled={!!busy}
                        onClick={() => run(`remind-${r.index}`, () => documentSigningService.remind(facilityId, id, r.index), reminded)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-[12px] text-slate-700 hover:bg-slate-200 disabled:opacity-50">
                        <Bell className="h-3.5 w-3.5" /> Remind
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {env.status === 'completed' && (
            <>
              <button type="button" disabled={!!busy} onClick={() => download('completed', `${fileBase} - signed.pdf`)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
                <Download className="h-4 w-4" /> Download signed copy
              </button>
              <button type="button" disabled={!!busy}
                onClick={() => run('copies', () => documentSigningService.emailCopiesAgain(facilityId, id), 'Signed copies emailed to everyone again.')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-100 text-slate-700 text-[13px] font-medium hover:bg-slate-200 disabled:opacity-50">
                <Mail className="h-4 w-4" /> Email signed copies again
              </button>
            </>
          )}
          {env.status !== 'completed' && (
            <button type="button" disabled={!!busy} onClick={() => download('source', `${fileBase}.pdf`)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-100 text-slate-700 text-[13px] font-medium hover:bg-slate-200 disabled:opacity-50">
              <Download className="h-4 w-4" /> Download document
            </button>
          )}
          {live && (
            <button type="button" disabled={!!busy}
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (!window.confirm('Cancel this document? Every signing link stops working. Anyone who already signed keeps their copy.')) return;
                run('cancel', () => documentSigningService.cancel(facilityId, id), 'Document cancelled.');
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-100 text-rose-700 text-[13px] font-medium hover:bg-rose-50 disabled:opacity-50">
              <XCircle className="h-4 w-4" /> Cancel document
            </button>
          )}
          {['draft', 'completed', 'voided', 'expired'].includes(env.status) && (
            <button type="button" disabled={!!busy}
              onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm(env.status === 'draft' ? 'Delete this draft?' : 'Remove this document from the list? Signed copies stay available to everyone who has a link.')) return;
                setBusy('delete');
                try {
                  await documentSigningService.remove(facilityId, id);
                  toast.success(env.status === 'draft' ? 'Draft deleted.' : 'Removed from the list.');
                  onBack();
                } catch (e) {
                  toast.error(errText(e, 'Could not remove it.'));
                  setBusy('');
                }
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-medium hover:bg-slate-200 disabled:opacity-50">
              <Trash2 className="h-4 w-4" /> {env.status === 'draft' ? 'Delete draft' : 'Remove from list'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------- send for signature */

const blankSigner = () => ({ name: '', email: '' });
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

const SendFlow = ({ facility, facilityId, onDone }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [signers, setSigners] = useState([blankSigner()]);
  const [order, setOrder] = useState('parallel');
  const [days, setDays] = useState(30);
  const [envelopeId, setEnvelopeId] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [fields, setFields] = useState([]);
  const [fromSigned, setFromSigned] = useState(false);
  const [busy, setBusy] = useState(false);

  const fromName = facility?.name || '';
  const fromEmail = facility?.sender_reply_to || facility?.facility_operator_email || facility?.sender_email || '';

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const setSigner = (i, key, value) =>
    setSigners((prev) => prev.map((s, j) => (j === i ? { ...s, [key]: value } : s)));

  const createDraft = async () => {
    if (!file) { toast.error('Choose a document.'); return; }
    if (!title.trim()) { toast.error('Give the document a title.'); return; }
    if (!fromEmail) { toast.error('This facility has no reply-to email. Add one in Settings → Communication.'); return; }
    if (signers.some((s) => !s.name.trim() || !emailOk(s.email))) {
      toast.error('Every signer needs a name and a valid email.'); return;
    }
    setBusy(true);
    try {
      const res = await documentSigningService.create(facilityId, file, {
        title: title.trim(),
        ...(message.trim() ? { message: message.trim() } : {}),
        from_party: { name: fromName, email: fromEmail },
        recipients: signers.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
        signing_order: order,
        expires_in_days: Number(days) || 30,
      });
      const id = res.data?.id;
      const pdf = await documentSigningService.pdf(facilityId, id, 'source');
      setEnvelopeId(id);
      setPdfUrl(URL.createObjectURL(pdf.data));
      setStep(2);
    } catch (e) {
      toast.error(errText(e, 'The document could not be prepared.'));
    } finally {
      setBusy(false);
    }
  };

  const hasFromBoxes = fields.some((f) => f.party === 'from');
  const missingSigner = signers.findIndex((_, i) =>
    !fields.some((f) => f.party === 'to' && f.recipient === i && f.kind === 'signature'));

  const send = async () => {
    if (missingSigner >= 0) {
      toast.error(`Place a signature box for ${signers[missingSigner].name || `signer ${missingSigner + 1}`}.`);
      return;
    }
    if (hasFromBoxes && !fromSigned) {
      toast.error('Your boxes are filled in when you sign. Click your signature box (or tick “Sign as”) first.');
      return;
    }
    setBusy(true);
    try {
      await documentSigningService.setFields(facilityId, envelopeId, toProvider(fields));
      if (hasFromBoxes) await documentSigningService.signAsSender(facilityId, envelopeId, fromName);
      const res = await documentSigningService.send(facilityId, envelopeId);
      const people = res.data?.recipients || [];
      const missed = people.filter((r) => !r.emailed);
      if (missed.length) {
        toast.warning(`Sent, but ${missed.length} email${missed.length > 1 ? 's' : ''} could not go out — see Tasks.`);
      } else {
        toast.success(`Sent. ${people.length} signer${people.length > 1 ? 's' : ''} emailed.`);
      }
      onDone(envelopeId);
    } catch (e) {
      toast.error(errText(e, 'The document could not be sent.'));
    } finally {
      setBusy(false);
    }
  };

  if (step === 2) {
    return (
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => onDone(envelopeId)} className="inline-flex items-center gap-1 text-[13px] text-blue-700">
            <ArrowLeft className="h-4 w-4" /> Save as draft
          </button>
          <p className="text-[13px] text-slate-500">Place a signature box for every signer, then send.</p>
        </div>
        <div className="rounded-xl border border-slate-200/80 bg-white p-4">
          <PlacementPages pdfUrl={pdfUrl} fields={fields} setFields={setFields}
            recipients={signers} fromName={fromName} fromSigned={fromSigned}
            onToggleFromSign={() => setFromSigned((v) => !v)} />
        </div>
        <div className="sticky bottom-0 mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
          {hasFromBoxes && (
            <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
              <input type="checkbox" checked={fromSigned} onChange={(e) => setFromSigned(e.target.checked)} />
              Sign as <strong>{fromName}</strong>
            </label>
          )}
          <span className="text-[12px] text-slate-500">
            {missingSigner >= 0
              ? `Still needed: a signature box for ${signers[missingSigner].name || `signer ${missingSigner + 1}`}`
              : 'Ready to send'}
          </span>
          <button type="button" onClick={send} disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
            <Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Send for signature'}
          </button>
        </div>
      </div>
    );
  }

  const input = 'mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]';
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-5 max-w-2xl space-y-4">
      <label className="block">
        <span className="text-[12px] font-medium text-slate-700">Document (PDF or Word)</span>
        <div className="mt-1 flex items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-[13px] text-slate-700 hover:bg-slate-200">
            <UploadCloud className="h-4 w-4" /> Choose file
            <input type="file" className="hidden"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
              }} />
          </label>
          <span className="text-[13px] text-slate-500 truncate">{file ? file.name : 'No file chosen'}</span>
        </div>
      </label>
      <label className="block">
        <span className="text-[12px] font-medium text-slate-700">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} className={input}
          placeholder="Rental agreement — unit B12" />
      </label>
      <label className="block">
        <span className="text-[12px] font-medium text-slate-700">Message to signers (optional)</span>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={3} className={input}
          placeholder="Please review and sign." />
      </label>
      <div>
        <span className="text-[12px] font-medium text-slate-700">Signers</span>
        {signers.map((s, i) => (
          <div key={i} className="mt-1.5 flex gap-2">
            <input value={s.name} onChange={(e) => setSigner(i, 'name', e.target.value)} placeholder="Full name"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
            <input type="email" value={s.email} onChange={(e) => setSigner(i, 'email', e.target.value)} placeholder="Email"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
            {signers.length > 1 && (
              <button type="button" aria-label="Remove signer" onClick={() => setSigners((prev) => prev.filter((_, j) => j !== i))}
                className="px-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
        {signers.length < 5 && (
          <button type="button" onClick={() => setSigners((prev) => [...prev, blankSigner()])}
            className="mt-2 inline-flex items-center gap-1 text-[13px] text-blue-700">
            <Plus className="h-4 w-4" /> Add signer
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        {signers.length > 1 && (
          <label className="block">
            <span className="text-[12px] font-medium text-slate-700">Signing order</span>
            <select value={order} onChange={(e) => setOrder(e.target.value)} className={input}>
              <option value="parallel">Everyone at once</option>
              <option value="sequential">One after another, in this order</option>
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-[12px] font-medium text-slate-700">Days to sign</span>
          <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className={`${input} w-28`} />
        </label>
      </div>
      <p className="text-[12px] text-slate-500">
        Sent from <strong>{fromName}</strong>{fromEmail ? ` (${fromEmail})` : ''}. Each signer gets their own link by email.
      </p>
      <button type="button" onClick={createDraft} disabled={busy}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
        {busy ? 'Preparing…' : 'Continue — place signature boxes'}
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ page */

const DocumentSigning = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id;
  const [params, setParams] = useSearchParams();
  const [connected, setConnected] = useState(null);
  const view = params.get('view') || 'documents';
  const openId = params.get('doc') || '';

  useEffect(() => {
    if (!facilityId) return undefined;
    let active = true;
    setConnected(null);
    documentSigningService.connection(facilityId)
      .then((res) => { if (active) setConnected(!!res.data?.connected); })
      .catch(() => { if (active) setConnected(false); });
    return () => { active = false; };
  }, [facilityId]);

  const go = (next) => setParams(next, { replace: false });

  if (!facilityId || connected === null) {
    return <p className="p-10 text-center text-[13px] text-slate-400">Loading…</p>;
  }
  if (!connected) {
    return (
      <AddOnPrompt icon={FileSignature} name="Document signing" appKey="esign"
        testId="signing-addon"
        summary="Send any document for electronic signature and track it to completion, right here."
        points={[
          'Upload a PDF or Word file and place signature, initials and date boxes',
          'Each signer gets their own secure link by email',
          'Everyone is emailed their signed copy automatically when the last person signs',
        ]} />
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Document signing</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">Send documents for signature and track them to completion</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => go({})}
            className={`px-3.5 py-2 rounded-lg text-[13px] font-medium ${view !== 'send' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
            Documents
          </button>
          <button type="button" onClick={() => go({ view: 'send' })}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-medium ${view === 'send' ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            <Send className="h-4 w-4" /> Send for signature
          </button>
        </div>
      </div>
      {view === 'send' ? (
        <SendFlow key={facilityId} facility={currentFacility} facilityId={facilityId}
          onDone={(id) => go(id ? { doc: id } : {})} />
      ) : openId ? (
        <DocumentDetail facilityId={facilityId} tz={currentFacility?.timezone} id={openId} onBack={() => go({})} />
      ) : (
        <DocumentList facilityId={facilityId} tz={currentFacility?.timezone} onOpen={(id) => go({ doc: id })} />
      )}
    </div>
  );
};

export default DocumentSigning;
